import { useMemo } from 'react'
import { useAdminUser } from './AdminContext'
import { accessOf, can, canAll, viewerFor, type Access } from '../core/rules/permissions'
import type { Viewer } from '../core/rules/staffAccess'
import type { Limits, Permission } from '../core/types/team'

export type CanControl = {
  access: Access
  /** De persoon bij dit account (voor "eigen" rijen). */
  staffId: string
  /** Heeft dit account de bevoegdheid (eigen of alles), of mag het deze rij? */
  can: (p: Permission, row?: { staffId?: string }) => boolean
  /** Voor alles, niet alleen eigen rijen. */
  canAll: (p: Permission) => boolean
  /** Een kijker voor de bestaande regels (`ownOnly`, `kassaDayFor`, `bookingProblem`). */
  viewer: (p: Permission) => Viewer
  limit: <K extends keyof Limits>(k: K) => Limits[K]
}

/**
 * Wat het ingelogde account mag: één bron voor menu, routes, zoeken en schermdelen
 * (core/rules/permissions.ts). Het beheer verbergt alleen; de server dwingt het af (403). Leest
 * alleen de sessie: rendert niet mee bij wijzigingen in het concept.
 */
export function useCan(): CanControl {
  const user = useAdminUser()
  return useMemo(() => {
    const access = accessOf(user)
    return {
      access,
      staffId: access.staffId,
      can: (p, row) => can(access, p, row),
      canAll: (p) => canAll(access, p),
      viewer: (p) => viewerFor(access, p),
      limit: (k) => access.limits[k],
    }
  }, [user])
}
